// runMotionDirector — executor for the agent's gated `motionDirectorScene`
// mutation, called by BulkMutateToolUI on the author's "Design" click (never
// auto-applied). Snapshots the LIVE scene (image boxes + their on-screen
// window, title, duration, narration offset, editor fonts) and POSTs it to
// aidea-middleware, which adds the narration words + brand colours, authorises
// every image URL and queues ONE paid design job.
//
//   POST {MW_API_URL}api/motion-director/scene → 202 { jobId, room }
//   job result (common/job_poll.es6 waitForJob): { navId, plan, dropped, adjustments, imageSwaps, warnings }

import { authedFetch } from "../../../common/http_service";
import { buildSceneTimeline, FPS } from "../../../common/sceneTimeline.es6";
import { LOCAL_FONTS, SUPPORTED_GOOGLE_FONTS } from "../../../common/font_extractor.es6";
import { getPluginImageUrl } from "../../utils/collectSceneImages.es6";
import { MDIR_ID_TAG } from "../../utils/motionImporter";
import { notifyInsufficientBalance } from "../../utils/insufficientBalance";

const MAX_IMAGES = 12; // = @ArrayMaxSize on the mw DTO
export const DENSITIES = ["key", "full"];
export const GRAPHICS = ["minimal", "balanced", "rich"];
export const TEXT_MODES = ["auto", "extract", "add"];
export const ICON_MODES = ["auto", "yes", "no"];
// ponytail: an unfinished card is only resumed for 30 min — GET /api/jobs/:id
// 404s once the Redis job expires and authedFetch toasts every 404.
const JOB_TTL_MS = 30 * 60 * 1000;
const round2 = (n) => Math.round(n * 100) / 100;
const isPctString = (v) => typeof v === "string" && /^-?\d+(\.\d+)?%$/.test(v.trim());
const isNumeric = (v) =>
  (typeof v === "number" && isFinite(v)) ||
  (typeof v === "string" && v.trim() !== "" && isFinite(Number(v)));
// The box's canvas-% rect, or null when it is not expressible as one (px / auto sizes, non-% position).
// widthUnit defaults to "%" in the reducer (plugin_toolbars_by_id.es6); heightUnit defaults to "px" for a
// non-resizable box, so a missing heightUnit does not count as %.
function boxRect(box, sz) {
  const pos = (box && box.position) || {};
  if (!isPctString(pos.x) || !isPctString(pos.y)) return null;
  if (!isNumeric(sz.width) || !isNumeric(sz.height)) return null;
  if ((sz.widthUnit || "%") !== "%" || sz.heightUnit !== "%") return null;
  const w = Number(sz.width);
  const h = Number(sz.height);
  return w > 0 && h > 0 ? { x: pct(pos.x), y: pct(pos.y), w, h } : null;
}
// "12%" -> 12 (same parse as common/extractTextLayers.es6)
const pct = (v) => {
  const n = parseFloat(String(v == null ? "" : v).replace("%", ""));
  return isFinite(n) ? n : 0;
};

// Training id from the `?id=` URL param (same source as runAnimateAllImages.js).
function readTrainingId() {
  try {
    return new URLSearchParams(window.location.search).get("id") || null;
  } catch {
    return null;
  }
}

/**
 * The POST body for one scene from the editor's present state, or null when the
 * scene does not exist. Skips data: URIs and the Motion Director's own boxes.
 */
export function collectMotionInputs(present, navId) {
  const navItem = present && present.navItemsById && present.navItemsById[navId];
  if (!navItem) return null;
  const timeline = buildSceneTimeline(navId, present);
  const durationSec = timeline.durationSec;
  const images = [];
  (navItem.boxes || []).forEach((boxId) => {
    if (boxId.includes(`-${MDIR_ID_TAG}-`)) return;
    const tb = (present.pluginToolbarsById || {})[boxId];
    const url = getPluginImageUrl(tb);
    if (!url || url.startsWith("data:")) return;
    const box = (present.boxesById || {})[boxId] || {};
    const tl = box.timeline || {};
    if (tl.visible === false) return;
    const startFrame = typeof tl.startFrame === "number" ? tl.startFrame : 0;
    const endSec =
      typeof tl.endFrame === "number" && tl.endFrame > startFrame ? tl.endFrame / FPS : durationSec;
    // The extract stage needs the image's canvas rect; a cropped / zoomed box has none.
    const st = tb.state || {};
    const tr = st.translate || {};
    const cropped = (st.scale && st.scale !== 1) || tr.x || tr.y;
    const rect = cropped ? null : boxRect(box, tb.structure || {});
    images.push({
      boxId,
      // the pre-swap original (so a re-run never extracts from an already-cleaned image), but only while
      // our cleaned image is still the one showing; a replaced image is sent as it is
      url: st.originalUrl && st.url === st.motionCleanUrl ? st.originalUrl : url,
      ...(rect ? { rect } : {}),
      startSec: round2(startFrame / FPS),
      endSec: round2(durationSec > 0 ? Math.min(endSec, durationSec) : endSec),
    });
  });
  // The narration file is the voiced soundList entry (voiceData/narrationText), not
  // blindly the first track (music may lead); fall back to the first when unidentifiable.
  const voiced = ((present.soundList || {})[navId] || []).find(
    (s) => s && s.file && !s.loopScene && (s.voiceData || s.narrationText)
  );
  const narration =
    (voiced && timeline.narration.find((n) => n.id && n.id === voiced.id)) || timeline.narration[0];
  return {
    navId,
    sceneTitle: String(
      ((present.viewToolbarsById || {})[navId] || {}).viewName || navItem.name || ""
    ).slice(0, 300),
    durationSec: round2(durationSec),
    narrationOffsetSec: narration ? round2(narration.startSec - (narration.offsetSec || 0)) : 0,
    editorFonts: [...LOCAL_FONTS, ...SUPPORTED_GOOGLE_FONTS],
    images: images.slice(0, MAX_IMAGES),
  };
}

/**
 * @returns {Promise<{ jobId: string } | { error: string }>}
 *   error is a code the card maps to friendly copy: "scene-missing" | "no-images" |
 *   "no-training" | "insufficient-balance" | "failed" (server text is never relayed).
 */
export async function runMotionDirector(_dispatch, getState, params, scope) {
  const state = typeof getState === "function" ? getState() : null;
  const present = state && state.undoGroup && state.undoGroup.present;
  const navId = scope && scope.navId;
  const inputs = present && navId ? collectMotionInputs(present, navId) : null;
  if (!inputs) return { error: "scene-missing" };
  if (!inputs.images.length) return { error: "no-images" };
  // Extract-only (Sync with AI toggle): mw lifts the image text + icons, no design; needs an uncropped image.
  const extractOnly = !!(params && params.extractOnly);
  if (extractOnly && !inputs.images.some((i) => i.rect)) return { error: "no-extractable" };
  const trainingId = readTrainingId();
  if (!trainingId) return { error: "no-training" };
  const density = params && params.density === "full" ? "full" : "key";
  const graphics = GRAPHICS.includes(params && params.graphics) ? params.graphics : "balanced";
  const text = extractOnly
    ? "extract"
    : TEXT_MODES.includes(params && params.text)
      ? params.text
      : "add";
  const icons = ICON_MODES.includes(params && params.icons) ? params.icons : "yes";
  if (!extractOnly) {
    // the toggle has no choices of its own: keep the author's last Motionize choices
    rememberChoice("density", trainingId, density);
    rememberChoice("graphics", trainingId, graphics);
    rememberChoice("text", trainingId, text);
    rememberChoice("icons", trainingId, icons);
  }
  try {
    const response = await authedFetch(`${process.env.MW_API_URL}api/motion-director/scene`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        trainingId,
        ...inputs,
        density,
        graphics,
        text,
        icons,
        ...(extractOnly ? { extractOnly: true } : {}),
        // mw dedupes on it: a re-sent Design click maps to the same job, no second charge
        ...(params && params.idempotencyKey
          ? {
              idempotencyKey: String(params.idempotencyKey)
                .replace(/[^A-Za-z0-9_-]/g, "_")
                .slice(0, 128),
            }
          : {}),
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = String(data.message || "");
      if (response.status === 402 || message.toLowerCase().includes("insufficient.balance")) {
        const details = data.details || {};
        notifyInsufficientBalance({
          requestedAmount: details.requiredCredits ?? "",
          currentBalance: details.availableCredits ?? "",
        });
        return { error: "insufficient-balance" };
      }
      return { error: "failed" };
    }
    return data.jobId ? { jobId: data.jobId } : { error: "failed" };
  } catch {
    return { error: "failed" };
  }
}

// Last card choice per training: aidea:motionDirectorDensity / …Graphics → { [trainingId]: value }.
const storeKey = (name) => `aidea:motionDirector${name[0].toUpperCase()}${name.slice(1)}`;

export function rememberChoice(name, trainingId, value) {
  try {
    const all = JSON.parse(localStorage.getItem(storeKey(name)) || "{}");
    all[trainingId] = value;
    localStorage.setItem(storeKey(name), JSON.stringify(all));
  } catch {
    /* best-effort */
  }
}

// Card preselection: the agent's explicit choice (envelope params[name]), else this
// training's last choice, else `fallback`.
export function initialChoice(params, name, allowed, fallback) {
  const asked = params && params[name];
  if (allowed.includes(asked)) return asked;
  try {
    const trainingId = readTrainingId();
    const last = trainingId && JSON.parse(localStorage.getItem(storeKey(name)) || "{}")[trainingId];
    return allowed.includes(last) ? last : fallback;
  } catch {
    return fallback;
  }
}

// The card's job lifecycle is stored on its own tool-call entry in the persisted chat
// thread (ChatPanel toolState, via ChatCommitContext); `save(entry | null)` writes it:
//   { at } (POST in flight) → { jobId, at } → { jobId, at, applied }
// What a (re)mounted card does with its entry.
export function motionResume(entry, now = Date.now()) {
  if (entry && typeof entry.applied === "number") return "applied";
  if (!entry || now - entry.at > JOB_TTL_MS) return "confirm";
  // no jobId yet: the POST may or may not have landed — re-sending the same key is safe
  return entry.jobId ? "poll" : "repost";
}

// One Design POST per card per page (the card remounts when the agent's reply finishes
// streaming, often mid-POST); across reloads the idempotencyKey dedupes on the server.
const designsInFlight = new Map();
export function startMotionDesign(key, save, dispatch, getState, params, scope) {
  if (!designsInFlight.has(key)) {
    save({ at: Date.now() });
    const p = runMotionDirector(dispatch, getState, { ...params, idempotencyKey: key }, scope)
      .catch(() => ({ error: "failed" }))
      .then((out) => {
        save(out.jobId ? { jobId: out.jobId, at: Date.now() } : null);
        return out;
      })
      .finally(() => designsInFlight.delete(key));
    designsInFlight.set(key, p);
  }
  return designsInFlight.get(key);
}

// Confirm-card copy from the envelope's params/cost — localized, so the card never
// shows the mw tool's English summary/label (those are written for the model).
export function motionCardCopy(params, cost, t) {
  const images = params && params.images;
  const credits = cost && cost.credits;
  return {
    summary:
      images > 0 ? t("aideaChat.motion.summary", { count: images }) : t("aideaChat.motion.confirm"),
    cost: Number.isFinite(credits) ? t("aideaChat.motion.cost", { credits }) : "",
  };
}
